import { useTranslate } from "ra-core";
import type { ReactElement } from "react";
import { SearchInput } from "@/components/admin/search-input";

import { useCustomFieldFilterInputs } from "../customFields/customFieldFilterInputs";
import { ChoiceFilter } from "../filters/ChoiceFilter";
import {
  useSalesFilterChoices,
  useStarChoices,
} from "../filters/commonFilters";
import { NULL_FILTER_VALUE } from "../filters/listFilterValue";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { getTranslatedCompanySizeLabel } from "./getTranslatedCompanySizeLabel";
import { sizes } from "./sizes";

/** Filters shown in the top bar until the user picks their own. */
export const DEFAULT_COMPANY_FILTERS = [
  "customer_status@in",
  "prospect_state@in",
  "lead_score@in",
  "sales_id@in",
];

const field = (name: string) => `resources.companies.fields.${name}`;

/** Every filter of the companies list; « Ajouter un filtre » shows or hides them. */
export const useCompanyFilters = (): ReactElement[] => {
  const translate = useTranslate();
  const {
    acquisitionChannels,
    companySectors,
    customerStatuses,
    eventTypes,
    prospectStates,
  } = useConfigurationContext();
  const starChoices = useStarChoices();
  const salesChoices = useSalesFilterChoices();
  const customFieldFilters = useCustomFieldFilterInputs("companies");
  const sizeChoices = sizes.map((size) => ({
    value: String(size.id),
    label: getTranslatedCompanySizeLabel(size, translate),
  }));

  return [
    <SearchInput key="q" source="q" alwaysOn />,
    <ChoiceFilter
      key="customer_status"
      source="customer_status@in"
      label={field("customer_status")}
      choices={customerStatuses}
    />,
    <ChoiceFilter
      key="prospect_state"
      source="prospect_state@in"
      label={field("prospect_state")}
      choices={prospectStates}
    />,
    <ChoiceFilter
      key="lead_score"
      source="lead_score@in"
      label={field("lead_score")}
      choices={starChoices}
    />,
    <ChoiceFilter
      key="sales_id"
      source="sales_id@in"
      label={field("sales_id")}
      choices={salesChoices}
      exclusiveValue={NULL_FILTER_VALUE}
    />,
    <ChoiceFilter
      key="event_types"
      source="event_types@ov"
      label={field("event_types")}
      choices={eventTypes}
      mode="array"
    />,
    <ChoiceFilter
      key="acquisition_channel"
      source="acquisition_channel@in"
      label={field("acquisition_channel")}
      choices={acquisitionChannels}
    />,
    <ChoiceFilter
      key="size"
      source="size@in"
      label={field("size")}
      choices={sizeChoices}
    />,
    <ChoiceFilter
      key="sector"
      source="sector@in"
      label={field("sector")}
      choices={companySectors}
    />,
    ...customFieldFilters,
  ];
};
